import { useToastStore } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { router } from '@/router';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  /** Дополнительные заголовки (напр. ключ Stripe, который живёт только в памяти вкладки). */
  headers?: Record<string, string>;
  /** When true, errors are surfaced via toast in addition to throwing. Default: true. */
  showError?: boolean;
  /** Internal: set after a token refresh to avoid infinite retry loops. */
  _retry?: boolean;
}

function buildUrl(path: string, query?: RequestOpts['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v != null) params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function isAuthEndpoint(path: string): boolean {
  return path.includes('/api/admin/auth/login') || path.includes('/api/admin/auth/refresh');
}

export async function request<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, query, showError = true, _retry = false } = opts;
  const auth = useAuthStore();

  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth.accessToken && !isAuthEndpoint(path)) {
    headers['Authorization'] = `Bearer ${auth.accessToken}`;
  }

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), init);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Network error';
    if (showError) useToastStore().error(msg);
    throw new ApiError(0, msg);
  }

  // Access-токен протух — пробуем один раз обновить его и повторить запрос.
  if (response.status === 401 && !isAuthEndpoint(path) && !_retry) {
    const refreshed = await auth.refresh();
    if (refreshed) {
      return request<T>(path, { ...opts, _retry: true });
    }
    auth.clear();
    if (router.currentRoute.value.name !== 'login') {
      router.push({ name: 'login' });
    }
    throw new ApiError(401, 'Session expired');
  }

  if (!response.ok) {
    let msg = `${response.status} ${response.statusText}`;
    try {
      const text = await response.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed.error === 'string') msg = parsed.error;
          else msg = text;
        } catch {
          msg = text;
        }
      }
    } catch { /* ignore */ }
    if (showError) useToastStore().error(msg);
    throw new ApiError(response.status, msg);
  }

  // 204 No Content
  if (response.status === 204) return undefined as unknown as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined as unknown as T;
  }
  return response.json() as Promise<T>;
}
